import { useCallback, useEffect, useState } from "react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { api, tradeAuth, connectToDeriv, toastApiError } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { OpenTrades } from "@/components/casino/OpenTrades";

const PAGE = 50;
const pnlOf = (t) => Number(t.sell_price || 0) - Number(t.buy_price || 0);
const fmtTime = (ep) => (ep ? new Date(Number(ep) * 1000).toLocaleString() : "—");

const Stat = ({ label, value, tone, testid }) => (
  <div className="casino-frame rounded-2xl p-5" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.3em] text-gold/70">{label}</div>
    <div className={`font-mono-naevo text-3xl mt-2 ${tone || "text-white"}`}>{value}</div>
  </div>
);

export default function History() {
  const active = tradeAuth();
  const accounts = (() => { try { return JSON.parse(localStorage.getItem("naevo_accounts") || "[]").filter((a) => a.account_id && a.account_type); } catch { return []; } })();
  const [viewId, setViewId] = useState(active?.account_id);
  const viewAcct = accounts.find((a) => a.account_id === viewId);
  const auth = active ? { ...active, account_id: viewId, type: viewAcct ? viewAcct.account_type : active.type } : null;
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async (offset = 0) => {
    if (!auth) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/account/history", { params: { session_id: auth.session_id, account_id: auth.account_id, limit: PAGE, offset } });
      const tx = r.data.transactions || [];
      setMeta({ account_id: r.data.account_id, account_type: r.data.account_type, currency: r.data.currency });
      setRows((xs) => (offset === 0 ? tx : [...xs, ...tx]));
      setDone(tx.length < PAGE);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load your Deriv history");
      toastApiError(toast, e, "Could not load your Deriv history");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.session_id, auth?.account_id]);

  useEffect(() => { load(0); }, [load]);

  const net = rows.reduce((s, t) => s + pnlOf(t), 0);
  const wins = rows.filter((t) => pnlOf(t) > 0).length;
  const cur = meta?.currency || "USD";

  return (
    <div>
      <SiteNav />
      <main className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
        <div className="text-xs uppercase tracking-[0.35em] text-gold/80">The Vault · Trade history</div>
        <h1 className="font-casino text-gold-grad text-4xl sm:text-5xl lg:text-6xl mt-2">Every trade you've made</h1>

        {!auth ? (
          <div className="casino-frame rounded-2xl p-8 mt-8 text-center" data-testid="history-login">
            <p className="text-white/70">Login with Deriv to see the full history of your Real and Demo trades.</p>
            <button onClick={() => connectToDeriv()} className="btn-gold rounded-full px-7 py-3 mt-5" data-testid="history-login-btn">Login with Deriv</button>
          </div>
        ) : (
          <>
            <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
              <div className="flex items-center gap-1 rounded-full border border-gold/40 bg-black/60 p-1" data-testid="history-tabs">
                {["real", "demo"].map((type) => {
                  const acct = accounts.find((a) => a.account_type === type);
                  const on = auth.type === type;
                  return (
                    <button key={type} disabled={!acct} onClick={() => { setRows([]); setViewId(acct.account_id); }} data-testid={`history-tab-${type}`}
                      className={`rounded-full px-4 py-1.5 text-xs font-semibold tracking-wider ${on ? (type === "demo" ? "bg-[#2AABEE] text-black" : "btn-gold") : "text-white/60"} ${!acct ? "opacity-40" : ""}`}>
                      {type.toUpperCase()}{acct ? ` · ${acct.account_id}` : ""}
                    </button>
                  );
                })}
              </div>
              <span className="text-[11px] text-white/45" data-testid="history-account">Showing only {auth.type === "demo" ? "Demo" : "Real"} trades · {meta?.account_id || auth.account_id}</span>
              <button onClick={() => load(0)} disabled={loading} className="ml-auto btn-ghost rounded-full px-4 py-2 text-xs flex items-center gap-2" data-testid="history-refresh">
                <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
              </button>
            </div>

            <OpenTrades auth={auth} onSold={() => load(0)} />

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
              <Stat label="Trades loaded" value={rows.length} testid="history-stat-count" />
              <Stat label="Wins" value={`${wins} / ${rows.length}`} testid="history-stat-wins" />
              <Stat label="Net P/L" value={error && !rows.length ? "—" : `${net >= 0 ? "+" : ""}${net.toFixed(2)} ${cur}`} tone={net >= 0 ? "text-[color:var(--naevo-success)]" : "text-[color:var(--naevo-danger)]"} testid="history-stat-net" />
            </div>

            <div className="casino-frame rounded-2xl mt-6 overflow-x-auto">
              <Table data-testid="history-table">
                <TableHeader>
                  <TableRow className="border-gold/10">
                    <TableHead className="text-gold/70">Time</TableHead>
                    <TableHead className="text-gold/70">Contract</TableHead>
                    <TableHead className="text-gold/70 text-right">Stake</TableHead>
                    <TableHead className="text-gold/70 text-right">Payout</TableHead>
                    <TableHead className="text-gold/70 text-right">P/L</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!loading && error && rows.length === 0 && (
                    <TableRow className="border-white/5">
                      <TableCell colSpan={5} className="text-center py-10" data-testid="history-error">
                        <div className="text-[color:var(--naevo-danger)]">{error}</div>
                        <button onClick={() => (error.toLowerCase().includes("login") || error.toLowerCase().includes("expired") || error.toLowerCase().includes("session") ? connectToDeriv() : load(0))} className="btn-gold rounded-full px-5 py-2 text-xs mt-3" data-testid="history-retry">
                          {error.toLowerCase().includes("expired") || error.toLowerCase().includes("session") ? "Login again" : "Retry"}
                        </button>
                      </TableCell>
                    </TableRow>
                  )}
                  {!loading && !error && rows.length === 0 && (
                    <TableRow className="border-white/5">
                      <TableCell colSpan={5} className="text-center text-white/40 py-10" data-testid="history-empty">No trades on this account yet.</TableCell>
                    </TableRow>
                  )}
                  {rows.map((t, i) => {
                    const p = pnlOf(t);
                    return (
                      <TableRow key={`${t.contract_id}-${i}`} className="border-white/5" data-testid={`history-row-${i}`}>
                        <TableCell className="font-mono-naevo text-xs text-white/60 whitespace-nowrap">{fmtTime(t.sell_time || t.purchase_time)}</TableCell>
                        <TableCell className="text-xs text-white/80 min-w-[220px]">
                          <div>{t.longcode || t.shortcode || t.contract_type}</div>
                          <div className="font-mono-naevo text-[10px] text-white/40">#{t.contract_id}</div>
                        </TableCell>
                        <TableCell className="text-right font-mono-naevo">{Number(t.buy_price || 0).toFixed(2)}</TableCell>
                        <TableCell className="text-right font-mono-naevo">{Number(t.sell_price || 0).toFixed(2)}</TableCell>
                        <TableCell className={`text-right font-mono-naevo ${p >= 0 ? "text-[color:var(--naevo-success)]" : "text-[color:var(--naevo-danger)]"}`}>{p >= 0 ? "+" : ""}{p.toFixed(2)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            <div className="mt-5 flex justify-center">
              {loading ? <Loader2 className="h-5 w-5 animate-spin text-gold" /> : !done && rows.length > 0 && (
                <button onClick={() => load(rows.length)} className="btn-gold rounded-full px-6 py-2.5 text-sm" data-testid="history-load-more">Load more</button>
              )}
            </div>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
